// src/pages/AcessoRemotoPage.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, addDoc, onSnapshot, doc, deleteDoc, updateDoc, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';
import PasswordInput from '../components/common/PasswordInput';
import PasswordDisplay from '../components/common/PasswordDisplay';
import TOTPDisplay from '../components/common/TOTPDisplay';
import QrScannerModal from '../components/common/QrScannerModal';
import LinkifyText from '../components/common/LinkifyText';


// --- Página de Acesso Remoto ---
export default function AcessoRemotoPage({ permissionLevel }) {
    const [accessList, setAccessList] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterClientId, setFilterClientId] = useState('all');
    const [copySuccessId, setCopySuccessId] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const { ConfirmationModal, showConfirmation } = useConfirmationModal();

    useEffect(() => {
        const fetchClients = async () => {
            const clientsRef = collection(db, `/artifacts/${appId}/public/data/clients`);
            const clientsAvulsosRef = collection(db, `/artifacts/${appId}/public/data/clients-avulsos`);
            
            const [clientsSnapshot, clientsAvulsosSnapshot] = await Promise.all([
                getDocs(clientsRef),
                getDocs(clientsAvulsosRef)
            ]);

            const combinedClients = [
                ...clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                ...clientsAvulsosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            ]
            .filter(c => c.ativo !== false);

            combinedClients.sort((a, b) => (a.nomeFantasia || a.name).localeCompare(b.nomeFantasia || b.name));
            setAllClients(combinedClients);
        };

        fetchClients();
    }, []);

    const remoteAccessCollectionPath = useMemo(() => `/artifacts/${appId}/public/data/remote_access`, []);
    useEffect(() => {
        const q = query(collection(db, remoteAccessCollectionPath), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAccessList(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [remoteAccessCollectionPath]);

    const handleOpenEditModal = (item) => {
        if (permissionLevel !== 'edit') return;
        setEditingItem(item);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditingItem(null);
        setIsEditModalOpen(false);
    };

    const handleDelete = (accessId, accessName) => {
        if (permissionLevel !== 'edit') return;
        showConfirmation({
            title: "Excluir Acesso?",
            message: `Tem a certeza que deseja apagar o acesso para "${accessName}"?`,
            onConfirm: async () => {
                await deleteDoc(doc(db, remoteAccessCollectionPath, accessId));
            }
        });
    };

    const handleConnectAndCopy = (access) => {
        if (access.password) {
            const textArea = document.createElement("textarea");
            textArea.value = access.password;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccessId(access.id);
                setTimeout(() => setCopySuccessId(null), 2000);
            } catch (err) {
                console.error('Falha ao copiar a senha:', err);
            }
            document.body.removeChild(textArea);
        }
        window.open(`teamviewer10://control?device=${access.teamViewerId.replace(/\s/g, '')}`, '_self');
    };

    const filteredAccessList = useMemo(() => {
        let list = accessList;

        if (filterClientId !== 'all') {
            list = list.filter(access => access.clientId === filterClientId);
        }

        if (searchTerm) {
            const lowercasedFilter = searchTerm.toLowerCase();
            list = list.filter(access =>
                access.clientName?.toLowerCase().includes(lowercasedFilter) ||
                access.machineName?.toLowerCase().includes(lowercasedFilter) ||
                access.userName?.toLowerCase().includes(lowercasedFilter)
            );
        }

        return list;
    }, [accessList, filterClientId, searchTerm]);

    return (
        <div className="relative min-h-full pt-8 md:pt-12 px-4 sm:px-6 md:px-8 pb-8">
            <ConfirmationModal />
            {isAddModalOpen && permissionLevel === 'edit' && (
                <AcessoRemotoFormModal
                    mode="add"
                    onClose={() => setIsAddModalOpen(false)}
                    allClients={allClients}
                    collectionPath={remoteAccessCollectionPath}
                />
            )}
            {isEditModalOpen && permissionLevel === 'edit' && (
                <AcessoRemotoFormModal
                    mode="edit"
                    item={editingItem}
                    onClose={handleCloseEditModal}
                    allClients={allClients}
                    collectionPath={remoteAccessCollectionPath}
                />
            )}
            <header className="mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-cyan-400">Acesso Remoto (TeamViewer)</h1>
                <p className="text-slate-400 mt-2">Cadastre e filtre os acessos remotos vinculados aos clientes.</p>
            </header>
            
            <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex-grow">
                    <label htmlFor="filterClient" className="block text-sm font-medium text-left mb-1 text-slate-300">Filtrar por Cliente</label>
                    <select
                        id="filterClient"
                        value={filterClientId}
                        onChange={(e) => setFilterClientId(e.target.value)}
                        className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                    >
                        <option value="all">Todos os Clientes</option>
                        {allClients.map(client => (
                            <option key={client.id} value={client.id}>{client.nomeFantasia || client.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-grow">
                    <label htmlFor="searchTerm" className="block text-sm font-medium text-left mb-1 text-slate-300">Pesquisar</label>
                    <input
                        id="searchTerm"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Pesquisar por cliente, máquina..."
                        className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                    />
                </div>
            </div>

            <div className="bg-slate-800 rounded-xl shadow-lg overflow-x-auto">
                 <table className="w-full text-left">
                    <thead className="bg-slate-700/50">
                        <tr>
                            <th className="p-4 text-sm font-semibold text-slate-300">Cliente Vinculado</th>
                            <th className="p-4 text-sm font-semibold text-slate-300">Nome da Máquina</th>
                            <th className="p-4 text-sm font-semibold text-slate-300">Usuário</th>
                            <th className="p-4 text-sm font-semibold text-slate-300">Conectar (Copia Senha)</th>
                            <th className="p-4 text-sm font-semibold text-slate-300">Senha</th>
                            {permissionLevel === 'edit' && <th className="p-4 text-sm font-semibold text-slate-300">Ações</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={permissionLevel === 'edit' ? 6 : 5} className="text-center p-4 text-slate-500">A carregar...</td></tr>
                        ) : filteredAccessList.length === 0 ? (
                            <tr><td colSpan={permissionLevel === 'edit' ? 6 : 5} className="text-center p-4 text-slate-500">Nenhum acesso remoto encontrado para o filtro selecionado.</td></tr>
                        ) : (
                            filteredAccessList.map((access) => (
                                <tr key={access.id} className="border-t border-slate-700">
                                    <td className="p-4 font-bold">{access.clientName}</td>
                                    <td className="p-4"><LinkifyText text={String(access.machineName || '')} /></td>
                                    <td className="p-4 text-slate-400"><LinkifyText text={String(access.userName || '')} /></td>
                                    <td className="p-4">
                                        <button
                                            onClick={() => handleConnectAndCopy(access)}
                                            className="text-cyan-400 font-bold hover:text-cyan-300 hover:underline"
                                        >
                                            {copySuccessId === access.id ? 'Senha Copiada!' : access.teamViewerId}
                                        </button>
                                    </td>
                                    <td className="p-4">
                                        <PasswordDisplay value={access.password} />
                                    </td>
                                    {permissionLevel === 'edit' && (
                                        <td className="p-4 flex items-center gap-2">
                                            <button onClick={() => handleOpenEditModal(access)} className="text-slate-400 hover:text-cyan-300 p-1">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>
                                            </button>
                                            <button onClick={() => handleDelete(access.id, access.machineName)} className="text-red-500 hover:text-red-400 p-1">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
             {permissionLevel === 'edit' && (
                <button 
                    onClick={() => setIsAddModalOpen(true)}
                    className="fixed bottom-8 right-8 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full p-4 shadow-lg transition-transform transform hover:scale-110 z-20"
                    title="Adicionar Novo Acesso"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                </button>
             )}
        </div>
    );
}

function AcessoRemotoFormModal({ mode = 'add', item, onClose, allClients, collectionPath }) {
    const isEdit = mode === 'edit';
    const [formData, setFormData] = useState({
        machineName: isEdit ? item.machineName || '' : '',
        userName: isEdit ? item.userName || '' : '',
        teamViewerId: isEdit ? item.teamViewerId || '' : '',
        clientId: isEdit ? item.clientId || '' : '',
        password: isEdit ? item.password || '' : '',
    });

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if (name === 'machineName') {
            setFormData({ ...formData, [name]: value.toUpperCase() });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (formData.machineName && formData.userName && formData.teamViewerId && formData.clientId) {
            const selectedClient = allClients.find(c => c.id === formData.clientId);
            if (!selectedClient) {
                alert("Cliente selecionado é inválido.");
                return;
            }
            
            const dataToSave = {
                machineName: formData.machineName,
                userName: formData.userName,
                teamViewerId: formData.teamViewerId,
                password: formData.password || '',
                clientId: formData.clientId,
                clientName: selectedClient.nomeFantasia || selectedClient.name,
            };

            if (isEdit) {
                const docRef = doc(db, collectionPath, item.id);
                await updateDoc(docRef, dataToSave);
            } else {
                dataToSave.createdAt = new Date();
                await addDoc(collection(db, collectionPath), dataToSave);
            }
            onClose();
        } else {
            alert('Por favor, preencha os campos obrigatórios (Cliente, Máquina, Usuário e ID).');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <header className="p-6 border-b border-slate-700">
                    <h3 className="font-bold text-2xl text-cyan-300">{isEdit ? 'Editando Acesso Remoto' : 'Adicionar Novo Acesso'}</h3>
                </header>
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-grow space-y-4">
                    <div>
                        <label htmlFor="clientId" className="block text-sm font-medium text-left mb-1 text-slate-300">Vincular ao Cliente</label>
                        <select
                            id="clientId" name="clientId"
                            value={formData.clientId} onChange={handleInputChange}
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                        >
                            <option value="">-- Selecione um cliente --</option>
                            {allClients.map(client => (
                                <option key={client.id} value={client.id}>{client.nomeFantasia || client.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="machineName" className="block text-sm font-medium text-left mb-1 text-slate-300">Nome da Máquina</label>
                        <input
                            id="machineName" type="text" name="machineName"
                            value={formData.machineName} onChange={handleInputChange}
                            placeholder="Ex: NOTEBOOK-FISCAL-02"
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label htmlFor="userName" className="block text-sm font-medium text-left mb-1 text-slate-300">Nome do Usuário</label>
                        <input
                            id="userName" type="text" name="userName"
                            value={formData.userName} onChange={handleInputChange}
                            placeholder="Ex: Ana Souza"
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-left mb-1 text-slate-300">Senha de Acesso (Opcional)</label>
                        <PasswordInput
                            id="password" name="password"
                            value={formData.password} onChange={handleInputChange}
                            placeholder="Senha para a conexão"
                        />
                    </div>
                    <div>
                        <label htmlFor="teamViewerId" className="block text-sm font-medium text-left mb-1 text-slate-300">ID do TeamViewer</label>
                        <input
                            id="teamViewerId" type="text" name="teamViewerId"
                            value={formData.teamViewerId} onChange={handleInputChange}
                            placeholder="Somente números"
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                        />
                    </div>
                </form>
                <footer className="p-6 border-t border-slate-700 mt-auto">
                    <div className="flex gap-4">
                        <button onClick={handleSubmit} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-md transition-colors">{isEdit ? 'Salvar Alterações' : 'Adicionar Acesso'}</button>
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-md transition-colors">Cancelar</button>
                    </div>
                </footer>
            </div>
        </div>
    );
}

